import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/axiosConfig";
import Navbar from "../components/common/Navbar";
import { toast } from "react-toastify";
import {
  ShieldCheck,
  RefreshCw,
  Users,
  UserCheck,
  UserX,
  Folder,
  Search,
  Check,
  RotateCcw,
  Trash2,
  ChevronLeft,
  ChevronRight,
  CheckCheck,
  AlertTriangle,
  LayoutDashboard,
  Phone,
  Edit2,
  UserPlus,
  Lock,
  Mail,
  Eye,
  EyeOff,
} from "lucide-react";
import "../styles/pages/SuperAdmin.css";

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const SuperAdmin = () => {
  const navigate = useNavigate();

  // Basic Platform Stats
  const [stats, setStats] = useState({
    totalUsers: 0,
    verifiedUsers: 0,
    unverifiedUsers: 0,
    totalGroups: 0,
    totalMedia: 0,
  });

  // Main Tab Navigation: "users" | "groups"
  const [mainTab, setMainTab] = useState("users");

  // Users Management State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, limit: 50 });

  // Groups Management State
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsSearch, setGroupsSearch] = useState("");
  const [groupsPage, setGroupsPage] = useState(1);
  const [groupsPagination, setGroupsPagination] = useState({ total: 0, pages: 1, limit: 30 });

  // Selection State (for Bulk user actions)
  const [selectedIds, setSelectedIds] = useState([]);

  // Modal State for User Verification / Edit Phone / Delete
  const [activeModal, setActiveModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");

  // Inspect Modals: User's Groups & Group's Members
  const [userGroupsModal, setUserGroupsModal] = useState(null);
  const [groupMembersModal, setGroupMembersModal] = useState(null);

  // Create User Modal State
  const [createUserData, setCreateUserData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "user",
    isVerified: true,
  });
  const [showPassword, setShowPassword] = useState(false);

  // 1. Fetch Stats
  const fetchStats = async () => {
    try {
      const res = await api.get("/admin/stats");
      if (res.data?.stats) {
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  // 2. Fetch Users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== "all") params.status = statusFilter;

      const res = await api.get("/admin/users", { params });
      if (res.data?.users) {
        setUsers(res.data.users);
      }
      if (res.data?.pagination) {
        setPagination(res.data.pagination);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error(err.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  // 3. Fetch Groups / Events (with automatic fallback to /groups/all)
  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const params = { page: groupsPage, limit: 50 };
      if (groupsSearch.trim()) params.search = groupsSearch.trim();

      let groupsList = null;
      let paginationData = null;

      // Primary Attempt: /admin/groups (includes populated members and photo aggregates)
      try {
        const res = await api.get("/admin/groups", { params });
        if (res.data?.groups && Array.isArray(res.data.groups) && res.data.groups.length > 0) {
          groupsList = res.data.groups;
          paginationData = res.data.pagination;
        }
      } catch (adminErr) {
        console.warn("Notice: /admin/groups failed, using direct /groups/all fallback:", adminErr.message);
      }

      // Seamless Fallback: /groups/all
      if (!groupsList) {
        const fallbackRes = await api.get("/groups/all");
        if (fallbackRes.data?.groups && Array.isArray(fallbackRes.data.groups)) {
          let list = fallbackRes.data.groups;
          if (groupsSearch.trim()) {
            const q = groupsSearch.trim().toLowerCase();
            list = list.filter(
              (g) => (g.name && g.name.toLowerCase().includes(q)) || (g.code && g.code.includes(q))
            );
          }
          groupsList = list.map((g) => ({
            ...g,
            joinedCount: (g.joinedUsers || []).length,
            permittedCount: (g.permittedUsers || []).length,
            photosCount: g.mediaCount || 0,
            photosSize: 0,
          }));
          paginationData = { total: groupsList.length, page: 1, pages: 1, limit: 50 };
        }
      }

      setGroups(groupsList || []);
      if (paginationData) {
        setGroupsPagination(paginationData);
      }
    } catch (err) {
      console.error("Error fetching groups:", err);
      toast.error(err.response?.data?.message || "Failed to load events/groups");
    } finally {
      setGroupsLoading(false);
    }
  }, [groupsPage, groupsSearch]);


  // Initial Load - Load Stats, Users and Groups immediately
  useEffect(() => {
    fetchStats();
    fetchGroups();
  }, [fetchGroups]);

  // Debounced Users Search / Filter
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchUsers]);

  // Reset page when search/filter changes
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  // Debounced Groups Search
  useEffect(() => {
    if (mainTab === "groups") {
      const timer = setTimeout(() => {
        fetchGroups();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [mainTab, fetchGroups]);

  useEffect(() => {
    setGroupsPage(1);
  }, [groupsSearch]);

  // User Groups Inspector Handler
  const handleOpenUserGroups = async (user) => {
    setUserGroupsModal({ open: true, user, groups: [], loading: true });
    try {
      const res = await api.get(`/admin/users/${user._id}/groups`);
      setUserGroupsModal({
        open: true,
        user,
        groups: res.data?.groups || [],
        loading: false,
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load user's events");
      setUserGroupsModal(null);
    }
  };

  // Group Members Inspector Handler
  const handleOpenGroupMembers = async (group) => {
    // Pre-populate any members that are already present on the group object
    const localMembersMap = new Map();

    if (group.createdBy) {
      const creatorObj = typeof group.createdBy === "object" ? group.createdBy : { _id: group.createdBy, name: "Admin" };
      localMembersMap.set(String(creatorObj._id || "creator"), {
        ...creatorObj,
        roleInGroup: "Creator / Admin",
        badgeColor: "purple",
      });
    }

    if (Array.isArray(group.joinedUsers)) {
      group.joinedUsers.forEach((u) => {
        if (u && typeof u === "object" && u._id) {
          localMembersMap.set(String(u._id), {
            ...u,
            roleInGroup: "Joined Member",
            badgeColor: "green",
          });
        }
      });
    }

    if (Array.isArray(group.permittedUsers)) {
      group.permittedUsers.forEach((u) => {
        if (u && typeof u === "object" && u._id && !localMembersMap.has(String(u._id))) {
          localMembersMap.set(String(u._id), {
            ...u,
            roleInGroup: "Permitted Access",
            badgeColor: "blue",
          });
        }
      });
    }

    const initialMembers = Array.from(localMembersMap.values());

    setGroupMembersModal({
      open: true,
      group,
      members: initialMembers,
      loading: initialMembers.length === 0,
    });

    try {
      const res = await api.get(`/admin/groups/${group._id}/members`);
      if (res.data?.members) {
        setGroupMembersModal({
          open: true,
          group: res.data?.group || group,
          members: res.data.members,
          loading: false,
        });
      } else {
        setGroupMembersModal((prev) => (prev ? { ...prev, loading: false } : null));
      }
    } catch (err) {
      console.warn("Could not fetch detailed members from API, using populated list:", err.message);
      setGroupMembersModal((prev) =>
        prev
          ? { ...prev, loading: false }
          : { open: true, group, members: initialMembers, loading: false }
      );
    }
  };

  // Selection Helpers
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const isAllSelected =
    users.length > 0 && users.every((u) => selectedIds.includes(u._id));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      const pageUserIds = new Set(users.map((u) => u._id));
      setSelectedIds((prev) => prev.filter((id) => !pageUserIds.has(id)));
    } else {
      const newIds = new Set([...selectedIds, ...users.map((u) => u._id)]);
      setSelectedIds(Array.from(newIds));
    }
  };

  // Single User Force Verify
  const handleForceVerifyConfirm = async () => {
    if (!activeModal?.user) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/users/${activeModal.user._id}/force-verify`);
      toast.success(res.data?.message || "User verified successfully!");
      setActiveModal(null);
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to force verify user");
    } finally {
      setActionLoading(false);
    }
  };

  // Resend Single OTP
  const handleResendOTP = async (user) => {
    try {
      const res = await api.post(`/admin/users/${user._id}/resend-otp`);
      toast.success(res.data?.message || `Fresh OTP generated: ${res.data?.otp}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    }
  };

  // Delete Single User
  const handleDeleteConfirm = async () => {
    if (!activeModal?.user) return;
    setActionLoading(true);
    try {
      const res = await api.delete(`/admin/users/${activeModal.user._id}`);
      toast.success(res.data?.message || "User deleted successfully!");
      setActiveModal(null);
      setSelectedIds((prev) => prev.filter((id) => id !== activeModal.user._id));
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete user");
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk Verify ALL Unverified Users
  const handleBulkVerifyAllConfirm = async () => {
    setActionLoading(true);
    try {
      const res = await api.post("/admin/users/bulk-verify-all");
      toast.success(res.data?.message || "All unverified users have been verified!");
      setActiveModal(null);
      setSelectedIds([]);
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to bulk verify users");
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk Verify Selected Users
  const handleBulkVerifySelectedConfirm = async () => {
    if (selectedIds.length === 0) return;
    setActionLoading(true);
    try {
      const res = await api.post("/admin/users/bulk-verify-selected", {
        userIds: selectedIds,
      });
      toast.success(res.data?.message || "Selected users verified successfully!");
      setActiveModal(null);
      setSelectedIds([]);
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to verify selected users");
    } finally {
      setActionLoading(false);
    }
  };

  // Edit Phone Number
  const handleOpenEditPhone = (user) => {
    setPhoneInput(user.phone || "");
    setActiveModal({ type: "editPhone", user });
  };

  const handleUpdatePhoneConfirm = async () => {
    if (!activeModal?.user) return;
    setActionLoading(true);
    try {
      const res = await api.put(`/admin/users/${activeModal.user._id}/phone`, {
        phone: phoneInput.trim(),
      });
      toast.success(res.data?.message || "Phone number updated successfully!");
      setActiveModal(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update phone number");
    } finally {
      setActionLoading(false);
    }
  };

  // Create User by Super Admin
  const handleCreateUserSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!createUserData.name.trim()) {
      toast.error("Please enter user's full name");
      return;
    }
    if (!createUserData.email.trim() && !createUserData.phone.trim()) {
      toast.error("Please enter either an Email address or Mobile number");
      return;
    }
    if (!createUserData.password || createUserData.password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setActionLoading(true);
    try {
      const res = await api.post("/admin/users/create", {
        name: createUserData.name.trim(),
        email: createUserData.email.trim() || undefined,
        phone: createUserData.phone.trim() || undefined,
        password: createUserData.password,
        role: createUserData.role,
        isVerified: createUserData.isVerified,
      });

      toast.success(res.data?.message || "User created successfully!");
      setActiveModal(null);
      setCreateUserData({
        name: "",
        email: "",
        phone: "",
        password: "",
        role: "user",
        isVerified: true,
      });
      fetchUsers();
      fetchStats();
    } catch (err) {
      console.error("❌ Create user error:", err);
      toast.error(err.response?.data?.message || "Failed to create user");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="super-admin-page">
      <Navbar />

      <main className="super-admin-container">
        {/* Top Header */}
        <div className="sa-header">
          <div className="sa-title-area">
            <div className="sa-title-badge-row">
              <span className="sa-badge-pro">
                <ShieldCheck size={14} /> SUPER ADMIN
              </span>
            </div>
            <h1>Control Console</h1>
            <p>Live user verification, bulk management, and event controls</p>
          </div>

          <div className="sa-header-actions">
            <button
              className="sa-btn-primary"
              onClick={() => {
                setCreateUserData({
                  name: "",
                  email: "",
                  phone: "",
                  password: "password123",
                  role: "user",
                  isVerified: true,
                });
                setShowPassword(false);
                setActiveModal({ type: "createUser" });
              }}
              title="Create a new user directly from Super Admin console"
            >
              <UserPlus size={16} />
              <span>Create User</span>
            </button>

            {stats.unverifiedUsers > 0 && (
              <button
                className="sa-btn-primary"
                onClick={() => setActiveModal({ type: "verifyAll" })}
                title="Verify all pending unverified users at once"
              >
                <CheckCheck size={16} />
                <span>Verify All Unverified ({stats.unverifiedUsers})</span>
              </button>
            )}

            <button
              className="sa-btn-secondary"
              onClick={() => navigate("/admin-dashboard")}
              title="Return to Admin Albums Dashboard"
            >
              <LayoutDashboard size={15} />
              <span>Albums Dashboard</span>
            </button>

            <button
              className="sa-btn-secondary"
              onClick={() => {
                fetchStats();
                if (mainTab === "users") fetchUsers();
                if (mainTab === "groups") fetchGroups();
              }}
              title="Refresh all metrics and table data"
            >
              <RefreshCw size={15} className={loading || groupsLoading ? "sa-spin" : ""} />
              <span>Refresh Data</span>
            </button>
          </div>
        </div>

        {/* Platform Summary Stats */}
        <div className="sa-stats-grid">
          <div className="sa-stat-card">
            <div className="sa-stat-icon purple">
              <Users size={22} />
            </div>
            <div className="sa-stat-info">
              <h3>{stats.totalUsers}</h3>
              <p>Total Registered Users</p>
            </div>
          </div>

          <div className="sa-stat-card">
            <div className="sa-stat-icon amber">
              <UserX size={22} />
            </div>
            <div className="sa-stat-info">
              <h3>{stats.unverifiedUsers}</h3>
              <p>Unverified Accounts (Pending)</p>
            </div>
          </div>

          <div className="sa-stat-card">
            <div className="sa-stat-icon green">
              <UserCheck size={22} />
            </div>
            <div className="sa-stat-info">
              <h3>{stats.verifiedUsers}</h3>
              <p>Active Verified Accounts</p>
            </div>
          </div>

          <div className="sa-stat-card">
            <div className="sa-stat-icon blue">
              <Folder size={22} />
            </div>
            <div className="sa-stat-info">
              <h3>{stats.totalGroups}</h3>
              <p>Total Albums ({stats.totalMedia} Photos)</p>
            </div>
          </div>
        </div>

        {/* 🔀 Main Navigation Tabs */}
        <div className="sa-main-nav-tabs">
          <button
            className={`sa-main-nav-btn ${mainTab === "users" ? "active" : ""}`}
            onClick={() => setMainTab("users")}
          >
            <Users size={17} />
            <span>Users Management</span>
            <span className="sa-main-nav-badge">{stats.totalUsers}</span>
          </button>

          <button
            className={`sa-main-nav-btn ${mainTab === "groups" ? "active" : ""}`}
            onClick={() => setMainTab("groups")}
          >
            <Folder size={17} />
            <span>Events & Groups</span>
            <span className="sa-main-nav-badge">{stats.totalGroups}</span>
          </button>
        </div>

        {/* TAB 1: USERS MANAGEMENT */}
        {mainTab === "users" && (
          <>
            {/* Search & Filter Control Bar */}
            <div className="sa-control-card">
              <div className="sa-search-wrapper">
                <Search size={16} className="sa-search-icon" />
                <input
                  type="text"
                  className="sa-search-input"
                  placeholder="Search by Name, Email, or Mobile Number..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="sa-filter-tabs">
                <button
                  className={`sa-tab-btn ${statusFilter === "all" ? "active" : ""}`}
                  onClick={() => setStatusFilter("all")}
                >
                  All Users ({stats.totalUsers})
                </button>
                <button
                  className={`sa-tab-btn ${statusFilter === "unverified" ? "active" : ""}`}
                  onClick={() => setStatusFilter("unverified")}
                >
                  Unverified ({stats.unverifiedUsers})
                </button>
                <button
                  className={`sa-tab-btn ${statusFilter === "verified" ? "active" : ""}`}
                  onClick={() => setStatusFilter("verified")}
                >
                  Verified ({stats.verifiedUsers})
                </button>
              </div>
            </div>

            {/* Users Table */}
            <div className="sa-table-wrapper">
              {loading ? (
                <div className="sa-empty">
                  <RefreshCw size={28} className="sa-spin" style={{ color: "#7c3aed" }} />
                  <p style={{ marginTop: "12px" }}>Loading users...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="sa-empty">
                  <Users size={36} style={{ color: "#cbd5e1" }} />
                  <p style={{ marginTop: "12px" }}>No matching users found.</p>
                </div>
              ) : (
                <>
                  <div className="sa-table-scroll-hint">
                    <span>⇄ Swipe horizontally to view all user details & actions</span>
                  </div>
                  <table className="sa-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}>
                        <input
                          type="checkbox"
                          className="sa-checkbox"
                          checked={isAllSelected}
                          onChange={toggleSelectAll}
                          title="Select / Deselect All visible users"
                        />
                      </th>
                      <th>User</th>
                      <th>Email</th>
                      <th>Mobile Number</th>
                      <th>Events & Groups</th>
                      <th>Status</th>
                      <th>OTP Status</th>
                      <th>Joined Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user._id}
                        style={selectedIds.includes(user._id) ? { background: "#faf5ff" } : {}}
                      >
                        {/* Checkbox */}
                        <td>
                          <input
                            type="checkbox"
                            className="sa-checkbox"
                            checked={selectedIds.includes(user._id)}
                            onChange={() => toggleSelect(user._id)}
                          />
                        </td>

                        {/* User Name & Role */}
                        <td>
                          <div className="sa-user-cell">
                            <div className="sa-user-avatar">
                              {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                            </div>
                            <div>
                              <div className="sa-user-name">
                                {user.name || "Anonymous User"}
                                <span className={`sa-role-badge ${user.role || "user"}`}>
                                  {user.role || "user"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Email */}
                        <td>
                          <div className="sa-email-cell">{user.email || "—"}</div>
                        </td>

                        {/* Mobile Number */}
                        <td>
                          {user.phone ? (
                            <div
                              className="sa-phone-cell clickable"
                              onClick={() => handleOpenEditPhone(user)}
                              title="Click to edit mobile number"
                            >
                              <Phone size={12} className="sa-phone-icon" />
                              <span>{user.phone}</span>
                              <Edit2 size={11} className="sa-phone-edit-icon" />
                            </div>
                          ) : (
                            <button
                              className="sa-phone-add-btn"
                              onClick={() => handleOpenEditPhone(user)}
                              title="Add mobile number for this user"
                            >
                              + Add Phone
                            </button>
                          )}
                        </td>

                        {/* Events & Groups Button */}
                        <td>
                          <button
                            className="sa-btn-groups-badge"
                            onClick={() => handleOpenUserGroups(user)}
                            title={`Inspect events created or joined by ${user.name || user.email}`}
                          >
                            <Folder size={13} />
                            <span>View Events</span>
                          </button>
                        </td>

                        {/* Status Badge */}
                        <td>
                          {user.isVerified ? (
                            <span className="sa-badge verified">● Verified</span>
                          ) : (
                            <span className="sa-badge unverified">● Unverified</span>
                          )}
                        </td>

                        {/* OTP Status */}
                        <td>
                          {!user.isVerified ? (
                            <div className="sa-otp-info">
                              OTP: <strong>{user.verifyOTP || "None"}</strong>
                            </div>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: "13px" }}>—</span>
                          )}
                        </td>

                        {/* Joined Date */}
                        <td style={{ fontSize: "13px", color: "#64748b" }}>
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
                        </td>

                        {/* Actions */}
                        <td>
                          <div className="sa-actions">
                            {!user.isVerified ? (
                              <>
                                <button
                                  className="sa-btn-table sa-btn-table-verify"
                                  title="Force Verify User"
                                  onClick={() => setActiveModal({ type: "verify", user })}
                                >
                                  <Check size={14} />
                                  <span>Verify</span>
                                </button>
                                <button
                                  className="sa-btn-table sa-btn-table-resend"
                                  title="Generate Fresh OTP"
                                  onClick={() => handleResendOTP(user)}
                                >
                                  <RotateCcw size={13} />
                                  <span>Resend OTP</span>
                                </button>
                              </>
                            ) : null}

                            <button
                              className="sa-btn-table sa-btn-table-delete"
                              title="Delete User"
                              onClick={() => setActiveModal({ type: "delete", user })}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

              {/* Pagination Controls */}
              {pagination.total > 0 && (
                <div className="sa-pagination-bar">
                  <div className="sa-pagination-info">
                    Showing <strong>{(page - 1) * pagination.limit + 1}</strong> to{" "}
                    <strong>{Math.min(page * pagination.limit, pagination.total)}</strong> of{" "}
                    <strong>{pagination.total}</strong> Users
                  </div>

                  <div className="sa-pagination-controls">
                    <button
                      className="sa-page-nav-btn"
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                      disabled={page <= 1}
                    >
                      <ChevronLeft size={15} />
                      <span>Previous</span>
                    </button>

                    <div className="sa-page-numbers">
                      {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                        .filter((p) => p === 1 || p === pagination.pages || Math.abs(p - page) <= 1)
                        .map((p, idx, arr) => (
                          <React.Fragment key={p}>
                            {idx > 0 && arr[idx - 1] !== p - 1 && (
                              <span className="sa-page-dots">...</span>
                            )}
                            <button
                              className={`sa-page-num ${p === page ? "active" : ""}`}
                              onClick={() => setPage(p)}
                            >
                              {p}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>

                    <button
                      className="sa-page-nav-btn"
                      onClick={() => setPage((prev) => Math.min(prev + 1, pagination.pages))}
                      disabled={page >= pagination.pages}
                    >
                      <span>Next</span>
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* TAB 2: EVENTS & GROUPS MANAGEMENT */}
        {mainTab === "groups" && (
          <>
            {/* Search Bar for Events */}
            <div className="sa-control-card">
              <div className="sa-search-wrapper" style={{ flex: 1 }}>
                <Search size={16} className="sa-search-icon" />
                <input
                  type="text"
                  className="sa-search-input"
                  placeholder="Search events by Event Name or Code (e.g. WED2025)..."
                  value={groupsSearch}
                  onChange={(e) => setGroupsSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Events Table */}
            <div className="sa-table-wrapper">
              {groupsLoading ? (
                <div className="sa-empty">
                  <RefreshCw size={28} className="sa-spin" style={{ color: "#7c3aed" }} />
                  <p style={{ marginTop: "12px" }}>Loading events and storage data...</p>
                </div>
              ) : groups.length === 0 ? (
                <div className="sa-empty">
                  <Folder size={36} style={{ color: "#cbd5e1" }} />
                  <p style={{ marginTop: "12px" }}>No matching events or groups found.</p>
                </div>
              ) : (
                <>
                  <div className="sa-table-scroll-hint">
                    <span>⇄ Swipe horizontally to view all event details & actions</span>
                  </div>
                  <table className="sa-table">
                  <thead>
                    <tr>
                      <th>Event / Group</th>
                      <th>Created By (Host)</th>
                      <th>Photos & Storage</th>
                      <th>Members & Access</th>
                      <th>Created Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((grp) => (
                      <tr key={grp._id}>
                        {/* Event Name & Code */}
                        <td>
                          <div className="sa-user-cell">
                            <div className="sa-user-avatar" style={{ background: "#f5f3ff", color: "#7c3aed" }}>
                              <Folder size={18} />
                            </div>
                            <div>
                              <div className="sa-user-name">
                                {grp.name}
                                <span className="sa-group-item-code">{grp.code}</span>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Created By */}
                        <td>
                          <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "14px" }}>
                            {grp.createdBy?.name || "Unknown Host"}
                          </div>
                          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                            {grp.createdBy?.email || "—"}
                            {grp.createdBy?.phone ? ` • ${grp.createdBy.phone}` : ""}
                          </div>
                        </td>

                        {/* Photos & Storage */}
                        <td>
                          <div style={{ fontWeight: 700, color: "#7c3aed", fontSize: "14px" }}>
                            {formatBytes(grp.photosSize)}
                          </div>
                          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                            {grp.photosCount?.toLocaleString()} Photos Uploaded
                          </div>
                        </td>

                        {/* Members Counts */}
                        <td>
                          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                            <span className="sa-group-role-badge green">
                              {grp.joinedCount} Joined
                            </span>
                            {grp.permittedCount > 0 && (
                              <span className="sa-group-role-badge blue">
                                {grp.permittedCount} Permitted
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Created Date */}
                        <td style={{ fontSize: "13px", color: "#64748b" }}>
                          {grp.createdAt ? new Date(grp.createdAt).toLocaleDateString() : "—"}
                        </td>

                        {/* Actions */}
                        <td>
                          <button
                            className="sa-btn-groups-badge"
                            onClick={() => handleOpenGroupMembers(grp)}
                            title="View all users, phones and emails in this group"
                          >
                            <Users size={13} />
                            <span>View Members</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

              {/* Groups Pagination Controls */}
              {groupsPagination.total > 0 && (
                <div className="sa-pagination-bar">
                  <div className="sa-pagination-info">
                    Showing <strong>{(groupsPage - 1) * groupsPagination.limit + 1}</strong> to{" "}
                    <strong>{Math.min(groupsPage * groupsPagination.limit, groupsPagination.total)}</strong> of{" "}
                    <strong>{groupsPagination.total}</strong> Events
                  </div>

                  <div className="sa-pagination-controls">
                    <button
                      className="sa-page-nav-btn"
                      onClick={() => setGroupsPage((prev) => Math.max(prev - 1, 1))}
                      disabled={groupsPage <= 1}
                    >
                      <ChevronLeft size={15} />
                      <span>Previous</span>
                    </button>

                    <div className="sa-page-numbers">
                      {Array.from({ length: groupsPagination.pages }, (_, i) => i + 1)
                        .filter((p) => p === 1 || p === groupsPagination.pages || Math.abs(p - groupsPage) <= 1)
                        .map((p, idx, arr) => (
                          <React.Fragment key={p}>
                            {idx > 0 && arr[idx - 1] !== p - 1 && (
                              <span className="sa-page-dots">...</span>
                            )}
                            <button
                              className={`sa-page-num ${p === groupsPage ? "active" : ""}`}
                              onClick={() => setGroupsPage(p)}
                            >
                              {p}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>

                    <button
                      className="sa-page-nav-btn"
                      onClick={() => setGroupsPage((prev) => Math.min(prev + 1, groupsPagination.pages))}
                      disabled={groupsPage >= groupsPagination.pages}
                    >
                      <span>Next</span>
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Floating Bulk Action Bar (when rows are selected) */}
      {selectedIds.length > 0 && (
        <div className="sa-floating-bar">
          <div className="sa-floating-count">
            <CheckCheck size={16} />
            <span>{selectedIds.length} users selected</span>
          </div>
          <div className="sa-floating-actions">
            <button
              className="sa-btn-primary sa-btn-floating"
              onClick={() => setActiveModal({ type: "verifySelected" })}
            >
              <CheckCheck size={16} />
              <span>Verify Selected ({selectedIds.length})</span>
            </button>
            <button
              className="sa-btn-ghost-dark"
              onClick={() => setSelectedIds([])}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Confirmation & Edit Modals */}
      {activeModal ? (
        <div className="sa-modal-overlay" onClick={() => setActiveModal(null)}>
          <div
            className="sa-modal"
            style={activeModal.type === "createUser" ? { maxWidth: 540 } : {}}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sa-modal-header">
              <div
                className={`sa-modal-icon ${
                  activeModal.type === "delete" ? "danger" : "primary"
                }`}
              >
                {activeModal.type === "delete" ? (
                  <AlertTriangle size={20} />
                ) : activeModal.type === "editPhone" ? (
                  <Phone size={20} />
                ) : activeModal.type === "createUser" ? (
                  <UserPlus size={20} />
                ) : (
                  <ShieldCheck size={20} />
                )}
              </div>
              <div>
                <h3 style={{ margin: 0 }}>
                  {activeModal.type === "createUser" && "Create New User"}
                  {activeModal.type === "verify" && "Force Verify User"}
                  {activeModal.type === "verifyAll" && "Verify All Unverified Users"}
                  {activeModal.type === "verifySelected" && "Verify Selected Users"}
                  {activeModal.type === "editPhone" && "Update Mobile Number"}
                  {activeModal.type === "delete" && "Confirm Delete User"}
                </h3>
                {activeModal.type === "createUser" && (
                  <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                    Register and activate a customer or administrator directly
                  </p>
                )}
              </div>
            </div>

            <div className="sa-modal-body">
              {activeModal.type === "createUser" && (
                <form
                  id="sa-create-user-form"
                  className="sa-create-user-form"
                  onSubmit={handleCreateUserSubmit}
                  autoComplete="off"
                >
                  <div className="sa-field-group">
                    <label className="sa-field-label">
                      Full Name <span className="req">*</span>
                    </label>
                    <div className="sa-input-wrapper">
                      <Users size={16} className="sa-input-icon" />
                      <input
                        type="text"
                        className="sa-form-input"
                        placeholder="e.g. Tanjil Bano"
                        value={createUserData.name}
                        onChange={(e) =>
                          setCreateUserData((prev) => ({ ...prev, name: e.target.value }))
                        }
                        autoFocus
                        required
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="sa-form-row">
                    <div className="sa-field-group">
                      <label className="sa-field-label">Email Address</label>
                      <div className="sa-input-wrapper">
                        <Mail size={16} className="sa-input-icon" />
                        <input
                          type="email"
                          className="sa-form-input"
                          placeholder="e.g. user@gmail.com"
                          value={createUserData.email}
                          onChange={(e) =>
                            setCreateUserData((prev) => ({ ...prev, email: e.target.value }))
                          }
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <div className="sa-field-group">
                      <label className="sa-field-label">Mobile Number</label>
                      <div className="sa-input-wrapper">
                        <Phone size={16} className="sa-input-icon" />
                        <input
                          type="tel"
                          className="sa-form-input"
                          placeholder="e.g. 9876543210"
                          value={createUserData.phone}
                          onChange={(e) =>
                            setCreateUserData((prev) => ({ ...prev, phone: e.target.value }))
                          }
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="sa-field-group">
                    <label className="sa-field-label">
                      Password <span className="req">*</span>
                    </label>
                    <div className="sa-input-wrapper">
                      <Lock size={16} className="sa-input-icon" />
                      <input
                        type={showPassword ? "text" : "password"}
                        className="sa-form-input"
                        placeholder="Min 6 characters (e.g. 123456)"
                        value={createUserData.password}
                        onChange={(e) =>
                          setCreateUserData((prev) => ({ ...prev, password: e.target.value }))
                        }
                        required
                        minLength={6}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="sa-pwd-toggle"
                        onClick={() => setShowPassword((prev) => !prev)}
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="sa-field-group">
                    <label className="sa-field-label">Select Account Role</label>
                    <div className="sa-role-cards">
                      <div
                        className={`sa-role-card ${createUserData.role === "user" ? "active" : ""}`}
                        onClick={() =>
                          setCreateUserData((prev) => ({ ...prev, role: "user" }))
                        }
                      >
                        <div className="sa-role-card-header">
                          <span className="sa-role-card-title">User</span>
                          {createUserData.role === "user" && <Check size={15} color="#7c3aed" />}
                        </div>
                        <p className="sa-role-card-desc">Normal customer access to events and photo sharing</p>
                      </div>

                      <div
                        className={`sa-role-card ${createUserData.role === "admin" ? "active" : ""}`}
                        onClick={() =>
                          setCreateUserData((prev) => ({ ...prev, role: "admin" }))
                        }
                      >
                        <div className="sa-role-card-header">
                          <span className="sa-role-card-title">Admin</span>
                          {createUserData.role === "admin" && <Check size={15} color="#7c3aed" />}
                        </div>
                        <p className="sa-role-card-desc">Super admin privileges across console & all albums</p>
                      </div>
                    </div>
                  </div>

                  <div className="sa-checkbox-box">
                    <label className="sa-checkbox-item">
                      <input
                        type="checkbox"
                        checked={createUserData.isVerified}
                        onChange={(e) =>
                          setCreateUserData((prev) => ({ ...prev, isVerified: e.target.checked }))
                        }
                      />
                      <div className="sa-checkbox-text">
                        <span className="sa-checkbox-title">Auto-Verify Account (Instant Activation)</span>
                        <span className="sa-checkbox-subtitle">Account will be activated immediately without OTP requirement</span>
                      </div>
                    </label>
                  </div>
                </form>
              )}

              {activeModal.type === "verify" && (
                <p>
                  Are you sure you want to manually mark <strong>{activeModal.user?.email || activeModal.user?.name}</strong> as verified? They will immediately be able to log in without entering an OTP.
                </p>
              )}

              {activeModal.type === "verifyAll" && (
                <p>
                  Are you sure you want to mark <strong>ALL {stats.unverifiedUsers} unverified users</strong> as verified at once? All pending accounts will immediately be unlocked and activated.
                </p>
              )}

              {activeModal.type === "verifySelected" && (
                <p>
                  Are you sure you want to mark the <strong>{selectedIds.length} selected users</strong> as verified?
                </p>
              )}

              {activeModal.type === "editPhone" && (
                <div>
                  <p style={{ marginBottom: "12px", color: "#475569", fontSize: "14px" }}>
                    Update mobile number for <strong>{activeModal.user?.name}</strong> ({activeModal.user?.email}):
                  </p>
                  <input
                    type="tel"
                    className="sa-search-input"
                    placeholder="e.g. 9876543210"
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    autoFocus
                    style={{ background: "#ffffff", border: "1px solid #cbd5e1" }}
                  />
                </div>
              )}

              {activeModal.type === "delete" && (
                <p>
                  Are you sure you want to permanently delete user <strong>{activeModal.user?.email || activeModal.user?.name}</strong>? This action cannot be undone.
                </p>
              )}
            </div>

            <div className="sa-modal-actions">
              <button
                type="button"
                className="sa-btn-secondary"
                onClick={() => setActiveModal(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              {activeModal.type === "createUser" ? (
                <button
                  type="submit"
                  form="sa-create-user-form"
                  className="sa-btn-primary"
                  disabled={actionLoading}
                >
                  <UserPlus size={15} />
                  <span>{actionLoading ? "Creating..." : "Create User"}</span>
                </button>
              ) : (
                <button
                  className={`sa-btn-primary ${activeModal.type === "delete" ? "sa-btn-danger" : ""}`}
                  onClick={() => {
                    if (activeModal.type === "verify") handleForceVerifyConfirm();
                    else if (activeModal.type === "verifyAll") handleBulkVerifyAllConfirm();
                    else if (activeModal.type === "verifySelected") handleBulkVerifySelectedConfirm();
                    else if (activeModal.type === "editPhone") handleUpdatePhoneConfirm();
                    else if (activeModal.type === "delete") handleDeleteConfirm();
                  }}
                  disabled={actionLoading}
                >
                  {actionLoading
                    ? "Processing..."
                    : activeModal.type === "delete"
                    ? "Delete Account"
                    : activeModal.type === "editPhone"
                    ? "Save Mobile Number"
                    : "Confirm Verify"}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* 📁 Modal: View User's Associated Events & Groups */}
      {userGroupsModal?.open && (
        <div className="sa-modal-overlay" onClick={() => setUserGroupsModal(null)}>
          <div className="sa-modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-header">
              <div className="sa-modal-icon primary">
                <Folder size={20} />
              </div>
              <div>
                <h3>Events for {userGroupsModal.user?.name || userGroupsModal.user?.email}</h3>
                <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
                  {userGroupsModal.user?.email} {userGroupsModal.user?.phone ? `• ${userGroupsModal.user.phone}` : ""}
                </p>
              </div>
            </div>

            <div className="sa-modal-body">
              {userGroupsModal.loading ? (
                <div className="sa-empty" style={{ padding: "30px" }}>
                  <RefreshCw size={24} className="sa-spin" style={{ color: "#7c3aed" }} />
                  <p style={{ marginTop: 8 }}>Loading events...</p>
                </div>
              ) : userGroupsModal.groups.length === 0 ? (
                <div className="sa-empty" style={{ padding: "30px" }}>
                  <Folder size={32} style={{ color: "#cbd5e1" }} />
                  <p style={{ marginTop: 8 }}>This user has not created or joined any events yet.</p>
                </div>
              ) : (
                <div className="sa-modal-groups-list">
                  {userGroupsModal.groups.map((grp) => (
                    <div key={grp._id} className="sa-modal-group-item">
                      <div>
                        <div className="sa-group-item-title">
                          <span>{grp.name}</span>
                          <span className="sa-group-item-code">{grp.code}</span>
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                          Created: {grp.createdAt ? new Date(grp.createdAt).toLocaleDateString() : "—"}
                        </div>
                      </div>
                      <span className={`sa-group-role-badge ${grp.badgeColor || "purple"}`}>
                        {grp.roleInGroup}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="sa-modal-actions">
              <button className="sa-btn-secondary" onClick={() => setUserGroupsModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 👥 Modal: View Group's Detailed Members */}
      {groupMembersModal?.open && (
        <div className="sa-modal-overlay" onClick={() => setGroupMembersModal(null)}>
          <div className="sa-modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-header">
              <div className="sa-modal-icon primary">
                <Users size={20} />
              </div>
              <div>
                <h3>Members in "{groupMembersModal.group?.name}"</h3>
                <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
                  Event Code: <strong style={{ fontFamily: "monospace" }}>{groupMembersModal.group?.code}</strong> • Total Participants: {groupMembersModal.members?.length || 0}
                </p>
              </div>
            </div>

            <div className="sa-modal-body" style={{ maxHeight: "450px", overflowY: "auto" }}>
              {groupMembersModal.loading ? (
                <div className="sa-empty" style={{ padding: "40px" }}>
                  <RefreshCw size={24} className="sa-spin" style={{ color: "#7c3aed" }} />
                  <p style={{ marginTop: 8 }}>Loading event members...</p>
                </div>
              ) : groupMembersModal.members.length === 0 ? (
                <div className="sa-empty" style={{ padding: "40px" }}>
                  <Users size={32} style={{ color: "#cbd5e1" }} />
                  <p style={{ marginTop: 8 }}>No members found in this event.</p>
                </div>
              ) : (
                <table className="sa-modal-members-table">
                  <thead>
                    <tr>
                      <th>Participant</th>
                      <th>Role in Event</th>
                      <th>Mobile Number</th>
                      <th>Email</th>
                      <th>Verification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupMembersModal.members.map((m) => (
                      <tr key={m._id}>
                        <td>
                          <div style={{ fontWeight: 600, color: "#0f172a" }}>
                            {m.name || "Anonymous User"}
                          </div>
                        </td>
                        <td>
                          <span className={`sa-group-role-badge ${m.badgeColor || "green"}`}>
                            {m.roleInGroup}
                          </span>
                        </td>
                        <td>
                          {m.phone ? (
                            <span style={{ fontWeight: 600, color: "#334155" }}>{m.phone}</span>
                          ) : (
                            <span style={{ color: "#94a3b8" }}>—</span>
                          )}
                        </td>
                        <td style={{ fontSize: "12px", color: "#475569" }}>
                          {m.email || "—"}
                        </td>
                        <td>
                          {m.isVerified ? (
                            <span className="sa-badge verified" style={{ fontSize: "11px", padding: "2px 8px" }}>
                              Verified
                            </span>
                          ) : (
                            <span className="sa-badge unverified" style={{ fontSize: "11px", padding: "2px 8px" }}>
                              Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="sa-modal-actions">
              <button className="sa-btn-secondary" onClick={() => setGroupMembersModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdmin;
